import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import ButtonWidgetStyle from '../WidgetEditComponents/ButtonWidgetStyle';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SimplisticHeaderEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // Image Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [isUploadingMobileBackground, setIsUploadingMobileBackground] = useState(false);
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SimplisticHeader initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 SimplisticHeader: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 SimplisticHeader: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 SimplisticHeader REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'centered-classic': {
                height: 700,
                heightMobile: 1200,
                paddingTop: 80,
                paddingTopMobile: 40,
                paddingBottom: 80,
                paddingBottomMobile: 40,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                // Text-Größen für zentriertes Layout
                titleSize: 72,
                titleSizeMobile: 48,
                titleWeight: '800',
                subtitleSize: 16,
                subtitleSizeMobile: 14,
                subtitleWeight: '300',
                // Icon-Größe für zentriertes Layout
                iconHeight: 80,
                // Visibility
                showIcon: true,
                showTitleText: true,
                showSubtitleText: true,
                showButtonText: true
            },
            'icon-left-text-right': {
                height: 600,
                heightMobile: 1200,
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                paddingBottomMobile: 40,
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                // Standard-Größen für seitliches Layout
                titleSize: 48,
                titleSizeMobile: 32,
                titleWeight: '700',
                subtitleSize: 20,
                subtitleSizeMobile: 16,
                subtitleWeight: '400',
                iconHeight: 60,
                showIcon: true,
                showTitleText: true,
                showSubtitleText: true,
                showButtonText: true
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ SimplisticHeader layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 SimplisticHeader layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Text Input Configuration
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'H2 - Main Title',
            type: 'input',
            placeholder: 'An extraordinary life starts with self care.',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        {
            fieldName: 'titleHighlightWords',
            label: 'H2 - Highlight Words',
            type: 'input',
            placeholder: 'self care, extraordinary (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'small', // Anderer Font-Typ als Main Title
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'subtitleText',
            label: 'H4 - Subtitle',
            type: 'textarea',
            placeholder: 'Discover three simple actions you can take right now to positively impact your body, mind and life.',
            rows: 3,
            fontField: 'subtitleFont',
            fontType: 'small',
            effectField: 'subtitleEffect'
        },
        {
            fieldName: 'buttonText',
            label: 'Button Text',
            type: 'input',
            placeholder: 'Enter button text...',
            fontField: 'buttonFont',
            fontType: 'small',
            buttonEffectField: 'buttonEffect'
        },
        {
            fieldName: 'buttonLink',
            label: 'Button Link',
            type: 'input',
            placeholder: 'https://example.com'
        }
    ];

    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#ffffff',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'subtitleColor',
                label: 'Subtitle',
                defaultValue: '#f0f0f0',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#D4A574',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'buttonColor',
                label: 'Button',
                defaultValue: '#D4A574',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'buttonTextColor',
                label: 'Button Text',
                defaultValue: '#FFFFFF',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'titleHighlightColor',
                label: 'Title Highlight',
                defaultValue: '#D4A574',
                icon: '/img/text-fat.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'H2 - Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '48',
            defaultWeight: '700'
        },
        {
            label: 'H2 - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '48', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'H4 - Subtitle',
            sizeField: 'subtitleSize',
            weightField: 'subtitleWeight',
            defaultSize: '20',
            defaultWeight: '400'
        }
    ];

    // 🚀 CUSTOM LAYOUT PROPERTIES - Keine widget-spezifischen Properties für SimplisticHeader
    const customLayoutProperties = [];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Non-Layout Properties + Icon Settings
    const advancedConfig = [
        // Icon Settings
        {
            fieldName: 'iconHeight',
            label: 'Icon Size',
            type: 'slider',
            defaultValue: 80,
            min: 30,
            max: 500,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'iconInvert',
            label: 'Invert Icon Colors',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/element.svg'
        },

        {
            fieldName: 'titleHighlightWeight',
            label: 'Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert für extreme Effekte
            max: 50,         // ✅ Erweitert für dramatische Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert für extreme Effekte
            max: 50,         // ✅ Erweitert für dramatische Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // Behavior Group
        {
            fieldName: 'textShadow',
            label: 'Text Shadow',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'iconShadow',
            label: 'Icon Shadow',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showIcon',
            label: 'Show Icon',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },

        // Display Group
        {
            fieldName: 'backgroundImageOpacity',
            label: 'Background Opacity',
            type: 'number',
            defaultValue: 100,
            min: 10,
            max: 100,
            step: 10,
            unit: '%',
            icon: '/img/background.svg'
        },
        {
            fieldName: 'backgroundImageBlur',
            label: 'Background Blur',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 10,
            step: 1,
            unit: 'px',
            icon: '/img/background.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="simplicisticHeader"
                    selectedLayout={editData.layoutType || 'centered-classic'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 BACKGROUND IMAGE CONTAINER - mit Opacity & Effects */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Image Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 100,
                            min: 10,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Opacity of the background image (text stays full opacity)',
                            convertValue: true,
                            color: '#8b5cf6',
                            icon: '/img/background.svg'
                        },
                        {
                            type: 'slider',
                            label: 'Image Blur',
                            fieldName: 'backgroundImageBlur',
                            defaultValue: 0,
                            min: 0,
                            max: 10,
                            step: 1,
                            unit: 'px',
                            description: 'Blur intensity of the background image',
                            convertValue: false,
                            color: '#06b6d4',
                            icon: '/img/background.svg'
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 🚀 MOBILE BACKGROUND IMAGE CONTAINER */}
                <ImageContainer
                    label="Mobile Background Image"
                    fieldName="mobileBackgroundImage"
                    currentImage={editData.mobileBackgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingMobileBackground}
                    setIsUploading={setIsUploadingMobileBackground}
                    controls={[]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 🚀 ICON CONTAINER - mit Toggle & Size */}
                <ImageContainer
                    label="Icon"
                    fieldName="iconImage"
                    currentImage={editData.iconImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingIcon}
                    setIsUploading={setIsUploadingIcon}
                    showToggle={true}
                    toggleLabel="Show Icon"
                    toggleFieldName="showIcon"
                    toggleChecked={editData.showIcon || false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'size',
                            label: 'Icon Size',
                            fieldName: 'iconHeight',
                            defaultValue: 60,
                            min: 30,
                            max: 120,
                            step: 5,
                            unit: 'px',
                            description: 'Height of the icon (width adjusts automatically)'
                        },
                        {
                            type: 'toggle',
                            label: 'Icon Shadow',
                            fieldName: 'iconShadow'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* Text Settings mit Toggle */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                />

                {/* 🚀 REVOLUTIONARY BUTTON STYLE SECTION */}
                <ButtonWidgetStyle 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    buttonConfig={{
                        textField: 'buttonText',
                        linkField: 'buttonLink'
                    }}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standardisierte Layout-Kontrollen */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* Animation Settings - Universal Component */}
                <AnimationSettingsSection 
                    editData={editData}
                    onUpdate={onUpdate}
                    showSpeedControls={true}
                    showDelayControls={true}
                    defaultExpanded={false}
                />

                {/* 🔧 ADVANCED SETTINGS - Non-Layout Properties only */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - Layout moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default SimplisticHeaderEdit;
